import React, { useState } from 'react';
import {
  FaHandshake, FaCheck, FaClock, FaBrain, FaChild, FaRocket,
  FaBriefcase, FaCreditCard, FaBuilding, FaHeartbeat, FaShieldAlt,
  FaUmbrellaBeach, FaUserGraduate, FaWallet, FaChartPie, FaFileMedicalAlt
} from 'react-icons/fa';
import FinancialHealthCheckupModal from './FinancialHealthCheckupModal';

const ServiceInterestsSection = ({ serviceInterests, handleMarkContacted }) => {
  const [checkupClient, setCheckupClient] = useState(null); // { id, name }

  if (!serviceInterests || serviceInterests.length === 0) return null;

  const serviceIcons = {
    'money-personality': FaBrain,
    'shiksha-sankalp': FaChild,
    'goal-manthan': FaRocket,
    'pathfinder': FaBriefcase,
    'debt-detox': FaCreditCard,
    'business-risk': FaBuilding,
    'financial-health': FaHeartbeat,
    'financial-health-detailed': FaHeartbeat,
    'insurance-portfolio': FaShieldAlt,
    'retirement-portfolio': FaUmbrellaBeach,
    'child-education-portfolio': FaUserGraduate,
    'wealth-portfolio': FaWallet,
    'mf-portfolio': FaChartPie
  };

  return (
    <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]" data-testid="service-interests-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
            <FaHandshake className="text-green-400 text-xl" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Client Service Interests</h2>
            <p className="text-gray-400 text-sm">Clients interested in value-added services</p>
          </div>
        </div>
        <span className="px-3 py-1 bg-green-500/20 text-green-400 font-bold rounded-full text-sm">
          {serviceInterests.filter(s => s.status === 'pending').length} Pending
        </span>
      </div>

      <div className="space-y-3 max-h-[300px] overflow-y-auto">
        {serviceInterests.map((interest) => {
          const Icon = serviceIcons[interest.service_id] || FaHandshake;
          const isPending = interest.status === 'pending';
          const isHealthCheckup =
            interest.service_id === 'financial-health-detailed' ||
            interest?.context?.source === 'financial-health-checkup';

          return (
            <div
              key={interest.id}
              className={`bg-[#0F172A] rounded-lg p-4 border ${isPending ? 'border-green-500/50' : 'border-[#2D5A8F]'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start space-x-3 min-w-0">
                  <div className={`w-10 h-10 rounded-lg ${isPending ? 'bg-green-500/20' : 'bg-gray-500/20'} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={isPending ? 'text-green-400' : 'text-gray-400'} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mb-1">
                      <span className="text-white font-bold">{interest.client_name}</span>
                      {isPending ? (
                        <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full animate-pulse">New Interest</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-gray-500/20 text-gray-400 text-xs rounded-full">Contacted</span>
                      )}
                      {isHealthCheckup && interest?.context?.risk_band && (
                        <span
                          className={`px-2 py-0.5 text-xs rounded-full border ${
                            interest.context.risk_band === 'critical'
                              ? 'bg-red-500/15 text-red-300 border-red-500/40'
                              : interest.context.risk_band === 'medium'
                              ? 'bg-amber-500/15 text-amber-300 border-amber-500/40'
                              : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                          }`}
                        >
                          {interest.context.risk_band.toUpperCase()} · {interest.context.score}/130
                        </span>
                      )}
                    </div>
                    <p className="text-[#FFD700] text-sm font-medium">{interest.service_title}</p>
                    <p className="text-gray-500 text-xs mt-1">
                      <FaClock className="inline mr-1" size={10} />
                      {new Date(interest.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0">
                  {isHealthCheckup && interest.client_id && (
                    <button
                      onClick={() => setCheckupClient({ id: interest.client_id, name: interest.client_name })}
                      className="px-3 py-2 bg-purple-500/20 text-purple-300 text-sm font-medium rounded-lg hover:bg-purple-500/30 transition-all flex items-center gap-1.5 border border-purple-500/40"
                      data-testid={`view-fhc-btn-${interest.id}`}
                    >
                      <FaFileMedicalAlt size={12} /><span>View Check-Up</span>
                    </button>
                  )}
                  {isPending && (
                    <button
                      onClick={() => handleMarkContacted(interest.id)}
                      className="px-3 py-2 bg-green-500/20 text-green-400 text-sm font-medium rounded-lg hover:bg-green-500/30 transition-all flex items-center gap-1.5"
                      data-testid={`mark-contacted-btn-${interest.id}`}
                    >
                      <FaCheck size={12} /><span>Mark Contacted</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {checkupClient && (
        <FinancialHealthCheckupModal
          clientId={checkupClient.id}
          clientName={checkupClient.name}
          onClose={() => setCheckupClient(null)}
        />
      )}
    </div>
  );
};

export default ServiceInterestsSection;
